import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const SupportAdmin = () => {
  const [supportRequests, setSupportRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchSupportRequests();
  }, []);

  const fetchSupportRequests = async () => {
    try {
      const response = await axios.get('/api/support', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setSupportRequests(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch support requests');
    }
  };

  const handleReply = async (id) => {
    try {
      await axios.post(
        `/api/support/${id}/reply`,
        { reply },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setSuccess('Reply sent successfully');
      setReply('');
      setSelectedRequest(null);
      fetchSupportRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reply');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0 opacity-10">
        <img
          src="/images/military plane cartoon illustration_4704680.png"
          alt="Military Plane"
          className="w-full h-full object-cover"
        />
      </div>

      <nav className="bg-gray-800/90 border-b border-gray-700 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <img
                  src="/images/military plane cartoon illustration_4704680.png"
                  alt="Plane Logo"
                  className="h-8 w-auto mr-3"
                />
                <h1 className="text-xl font-bold text-yellow-500">Support Requests</h1>
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="ml-4 px-4 py-2 text-sm text-gray-300 hover:text-yellow-500 transition-colors duration-200"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 relative z-10">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-gray-800/90 shadow-lg rounded-lg p-8 border border-gray-700 backdrop-blur-sm">
            {error && (
              <div className="mb-4 p-4 bg-red-900/50 border border-red-700 rounded-md text-red-200 text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 p-4 bg-green-900/50 border border-green-700 rounded-md text-green-200 text-sm">
                {success}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {supportRequests.map((request) => (
                    <tr key={request.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {new Date(request.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {request.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {request.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {request.subject}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            request.reply
                              ? 'bg-green-900/50 text-green-200'
                              : 'bg-yellow-900/50 text-yellow-200'
                          }`}
                        >
                          {request.reply ? 'Replied' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        <button
                          onClick={() => setSelectedRequest(request)}
                          className="text-yellow-500 hover:text-yellow-600"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Reply Modal */}
            {selectedRequest && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                  <div
                    className="fixed inset-0 transition-opacity"
                    onClick={() => setSelectedRequest(null)}
                  >
                    <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
                  </div>

                  <div className="inline-block align-bottom bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div>
                      <h3 className="text-lg font-medium text-yellow-500 mb-4">Support Request Details</h3>
                      <div className="mb-4">
                        <p className="text-sm text-gray-300">
                          <strong>From:</strong> {selectedRequest.name} ({selectedRequest.email})
                        </p>
                        <p className="text-sm text-gray-300">
                          <strong>Subject:</strong> {selectedRequest.subject}
                        </p>
                        <p className="text-sm text-gray-300 mt-2">
                          <strong>Message:</strong>
                        </p>
                        <p className="text-sm text-gray-300 mt-1">{selectedRequest.message}</p>
                      </div>

                      {selectedRequest.reply && (
                        <div className="mb-4">
                          <p className="text-sm text-gray-300">
                            <strong>Previous Reply:</strong>
                          </p>
                          <p className="text-sm text-gray-300 mt-1">{selectedRequest.reply}</p>
                        </div>
                      )}

                      <div className="mt-4">
                        <label htmlFor="reply" className="block text-sm font-medium text-gray-300">
                          Reply
                        </label>
                        <textarea
                          id="reply"
                          rows={4}
                          value={reply}
                          onChange={(e) => setReply(e.target.value)}
                          className="mt-1 block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        />
                      </div>

                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="button"
                          onClick={() => handleReply(selectedRequest.id)}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-500 text-base font-medium text-gray-900 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                          Send Reply
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedRequest(null)}
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-600 shadow-sm px-4 py-2 bg-gray-700 text-base font-medium text-gray-300 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:w-auto sm:text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SupportAdmin;
